import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase credentials');
}

// Short-lived cache for GET requests to avoid duplicate requests during initial load.
// Also deduplicate in-flight GETs so concurrent identical calls share the same promise.
const GET_CACHE_TTL = 2000; // 2 seconds
const inFlight = new Map<string, Promise<Response>>();
const getCache = new Map<string, { timestamp: number; response: Response }>();

export const customFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const urlStr =
    typeof input === 'string'
      ? input
      : input instanceof URL
      ? input.toString()
      : input instanceof Request
      ? input.url
      : String(input);

  const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
  const isAuth = urlStr.includes('/auth/');
  const isGet = method === 'GET';
  const key = `${method}:${urlStr}`;

  // Serve from short-lived cache for GET non-auth endpoints
  if (isGet && !isAuth) {
    const cached = getCache.get(key);
    if (cached && Date.now() - cached.timestamp < GET_CACHE_TTL) {
      return cached.response.clone();
    }
  }

  // Deduplicate in-flight GET requests (including auth GETs like /auth/v1/user)
  if (isGet) {
    const inflight = inFlight.get(key);
    if (inflight) {
      const shared = await inflight;
      return shared.clone();
    }
  }

  const promise = (async () => {
    const response = await fetch(input as any, init);

    // For auth endpoints, return the response even if not OK (so caller can read error body)
    if (isAuth && !response.ok) {
      return response;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Cache successful GET responses for a brief period (non-auth only)
    if (isGet && !isAuth) {
      const cloneForCache = response.clone();
      getCache.set(key, { timestamp: Date.now(), response: cloneForCache });
    }

    return response;
  })();

  if (isGet) {
    inFlight.set(key, promise);
  }

  try {
    const res = await promise;
    return res.clone();
  } finally {
    if (isGet) {
      inFlight.delete(key);
    }
  }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    fetch: customFetch
  }
});